import { IQuickTimeSlot } from '@jooxter/utils';
import { useMemo } from 'react';
import { IUseResourceSlotGenerator } from './types';
import { useDefaultSlotGenerator } from './useDefaultSlotGenerator';
import { useFetchFreeSlots } from './useFetchFreeSlots';
import { getResourceSlots, sortSlots } from './helpers';

export const useResourceSlotGenerator = ({
  start,
  end,
  options,
}: IUseResourceSlotGenerator): { slots: IQuickTimeSlot[]; isLoading: boolean } => {
  const { slots: defaultSlots, isLoading: isDefaultSlotLoading } = useDefaultSlotGenerator({
    start,
    end,
    timezone: options.timezone,
  });
  const { dayDefaultSlot, amDefaultSlot, pmDefaultSlot } = sortSlots(defaultSlots);

  const { slots: resourceDaySlots, isLoading: isResourceDaySlotsLoading } = useFetchFreeSlots(
    dayDefaultSlot,
    options.timezone,
    dayDefaultSlot?.start && dayDefaultSlot.end
      ? {
          id: options.resourceId,
          start: dayDefaultSlot.start,
          end: dayDefaultSlot.end,
          seatRequested: options.seatRequested,
          bookingMask: options.bookingMask,
        }
      : undefined
  );
  const { slots: resourceAmSlots, isLoading: isResourceAmSlotsLoading } = useFetchFreeSlots(
    amDefaultSlot,
    options.timezone,
    amDefaultSlot?.start && amDefaultSlot.end
      ? {
          id: options.resourceId,
          start: amDefaultSlot.start,
          end: amDefaultSlot.end,
          seatRequested: options.seatRequested,
          bookingMask: options.bookingMask,
        }
      : undefined
  );
  const { slots: resourcePmSlots, isLoading: isResourcePmSlotsLoading } = useFetchFreeSlots(
    pmDefaultSlot,
    options.timezone,
    pmDefaultSlot?.start && pmDefaultSlot.end
      ? {
          id: options.resourceId,
          start: pmDefaultSlot.start,
          end: pmDefaultSlot.end,
          seatRequested: options.seatRequested,
          bookingMask: options.bookingMask,
        }
      : undefined
  );

  const slots = useMemo(() => {
    if (!defaultSlots?.length) {
      return [];
    }

    return getResourceSlots({
      daySlots: {
        dayDefaultSlot,
        resourceDaySlots,
      },
      amSlots: {
        amDefaultSlot,
        resourceAmSlots,
      },
      pmSlots: {
        pmDefaultSlot,
        resourcePmSlots,
      },
    });
  }, [
    amDefaultSlot,
    dayDefaultSlot,
    defaultSlots?.length,
    pmDefaultSlot,
    resourceAmSlots,
    resourceDaySlots,
    resourcePmSlots,
  ]);

  return {
    slots,
    isLoading:
      isDefaultSlotLoading || isResourceDaySlotsLoading || isResourceAmSlotsLoading || isResourcePmSlotsLoading,
  };
};
